import {useRef, useState} from 'react';

import {useLatestValue, useOnMount} from 'shared/web';

/**
 * Runs at most one request per bulk operation for the tracking lifetime. A request answered after
 * its owner unmounted calls neither handler, and its operation no longer counts as pending.
 */
export function usePendingBulkOperationRequests<
  Input extends {bulkOperationId: string},
  Result,
>(args: {
  request: (input: Input) => Promise<Result>;
  onSuccess: (input: Input, result: Result) => void;
  onError: (input: Input, error: unknown) => void;
}) {
  const latest = useLatestValue(args);
  const owner = useRef({active: true, generation: 0, pending: new Set<string>()});
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  useOnMount(
    () => {
      owner.current.active = true;
    },
    () => {
      owner.current.active = false;
      owner.current.generation += 1;
      owner.current.pending.clear();
    }
  );

  const canStart = (bulkOperationId: string) =>
    owner.current.active && !owner.current.pending.has(bulkOperationId);

  const start = async (input: Input) => {
    const lifecycle = owner.current;
    const bulkOperationId = input.bulkOperationId;
    if (!canStart(bulkOperationId)) {
      return;
    }
    const generation = lifecycle.generation;
    const isCurrent = () => lifecycle.active && lifecycle.generation === generation;
    lifecycle.pending.add(bulkOperationId);
    setPendingIds(new Set(lifecycle.pending));
    // try also catches a request that throws before returning its promise.
    try {
      const result = await latest.current.request(input);
      if (isCurrent()) {
        latest.current.onSuccess(input, result);
      }
    } catch (error) {
      if (isCurrent()) {
        latest.current.onError(input, error);
      }
    } finally {
      if (isCurrent()) {
        lifecycle.pending.delete(bulkOperationId);
        setPendingIds(new Set(lifecycle.pending));
      }
    }
  };

  return {pendingIds, canStart, start};
}
